/**
 * Standalone Profile page — /profile
 *
 * Implements designs/Profile.html.
 * Backed by GET /api/me/stats (Task 2.6) and GET /api/projects.
 */

import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { Avatar } from "@/components/Avatar";
import { Button } from "@/components/Button";
import { CompletionBarChart } from "@/components/CompletionBarChart";
import { MetricCard } from "@/components/MetricCard";
import { Skeleton } from "@/components/Skeleton";
import { Icon } from "@/components/Icon";

function ProfilePage() {
  const me = useQuery({ queryKey: queryKeys.me(), queryFn: api.me });
  const stats = useQuery({
    queryKey: queryKeys.profileStats(),
    queryFn: api.profileStats,
  });
  const teamsQ = useQuery({ queryKey: ["teams"], queryFn: () => api.teams() });
  const projs = useQuery({
    queryKey: queryKeys.myProjects(),
    queryFn: api.myProjects,
  });

  if (!me.data) {
    return (
      <div className="pg-wrap pg-profile" style={{ padding: "30px 32px" }}>
        <Skeleton height={84} />
      </div>
    );
  }

  const u = me.data.user;
  const displayName = u.name ?? u.email;
  const statsData = stats.data;
  const projects = projs.data?.items ?? [];

  return (
    <div className="pg-wrap pg-profile">
      {/* ── Profile header ── */}
      <div className="prof-head">
        <Avatar id={u.id} name={displayName} size={84} image={u.image} />
        <div className="prof-id">
          <h1>{displayName}</h1>
          <div className="role">{u.role ?? "Member"}</div>
          <div className="sub">
            <span>
              <Icon name="mail" size={14} />
              {u.email}
            </span>
            <span>
              <Icon name="pin" size={14} />—
            </span>
            <span>
              <Icon name="cal" size={14} />
              Member since
            </span>
          </div>
        </div>
        <div className="prof-actions">
          <Button variant="secondary" size="sm">
            <Icon name="chat" size={14} /> Message
          </Button>
          <Link
            to="/settings/account"
            className="btn btn-primary btn-sm"
            style={{ textDecoration: "none" }}
          >
            <Icon name="settings" size={14} /> Edit profile
          </Link>
        </div>
      </div>

      <div className="prof-body">
        {/* ── 4-up stat row ── */}
        <div className="stat-row">
          {stats.isLoading ? (
            <Skeleton count={4} height={88} />
          ) : statsData ? (
            <>
              <MetricCard
                label="Tasks completed"
                value={statsData.completed}
                footer={{ text: "all time", tone: "flat" }}
              />
              <MetricCard
                label="On-time rate"
                value={`${statsData.onTimeRate}%`}
                footer={{ text: "on track", tone: "up", icon: "check" }}
              />
              <MetricCard
                label="Active projects"
                value={statsData.activeProjects}
                footer={{ text: "in progress", tone: "flat" }}
              />
              <MetricCard
                label="Comments"
                value={statsData.commentsAuthored}
                footer={{ text: "this quarter", tone: "flat" }}
              />
            </>
          ) : null}
        </div>

        {/* ── Main grid: chart+projects left, details+passkeys right ── */}
        <div className="grid2">
          {/* Left column */}
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {/* Completion bar chart */}
            <div className="panel">
              <div className="panel-head">
                <h3>Tasks completed · last 6 months</h3>
              </div>
              <div className="panel-pad">
                {stats.isLoading ? (
                  <Skeleton height={140} />
                ) : statsData ? (
                  <CompletionBarChart data={statsData.monthly} />
                ) : null}
              </div>
            </div>

            {/* My teams */}
            {(() => {
              const myTeams = (teamsQ.data?.teams ?? []).filter((t) =>
                (t.members ?? []).some((m) => m.userId === u.id),
              );
              if (myTeams.length === 0) return null;
              return (
                <div className="panel">
                  <div className="panel-head">
                    <h3>Teams</h3>
                  </div>
                  <div
                    className="panel-body"
                    style={{ display: "flex", flexWrap: "wrap", gap: 8 }}
                  >
                    {myTeams.map((t) => {
                      const lead = (t.members ?? []).find(
                        (m) => m.userId === u.id,
                      )?.isLead;
                      return (
                        <span key={t.id} className="tag">
                          {t.name}
                          {lead ? " · lead" : ""}
                        </span>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Assigned projects */}
            <div className="panel">
              <div className="panel-head">
                <h3>Assigned projects</h3>
                <Link to="/projects" className="lk">
                  All →
                </Link>
              </div>
              <div className="panel-body">
                {projs.isLoading ? (
                  <Skeleton count={3} height={48} />
                ) : (
                  projects.map((p) => {
                    const completed = p.completedCount ?? 0;
                    const total = p.totalCount ?? 0;
                    const pct =
                      total > 0 ? Math.round((completed / total) * 100) : 0;
                    const open = Math.max(0, total - completed);
                    return (
                      <div key={p.id} className="pj">
                        <div
                          className="pj-ic"
                          style={{
                            background: p.color ?? "var(--green-500)",
                          }}
                        >
                          <Icon name="folder" size={15} />
                        </div>
                        <div>
                          <div className="pj-name">{p.name}</div>
                          <div className="pj-meta">
                            {p.status} · {open} open task
                            {open === 1 ? "" : "s"}
                          </div>
                        </div>
                        <div className="pj-prog">
                          <div
                            className="progress"
                            role="progressbar"
                            aria-label={`${p.name} progress`}
                            aria-valuenow={pct}
                            aria-valuemin={0}
                            aria-valuemax={100}
                          >
                            <i style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {/* Right column — Personal details + Passkeys */}
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div className="panel">
              <div className="panel-head">
                <h3>Personal details</h3>
                <Link to="/settings/account" className="lk">
                  Edit
                </Link>
              </div>
              <div className="panel-body">
                <div className="det">
                  <span className="k">Full name</span>
                  <span className="v">{u.name ?? "—"}</span>
                </div>
                <div className="det">
                  <span className="k">Role</span>
                  <span className="v">{u.role ?? "—"}</span>
                </div>
                <div className="det">
                  <span className="k">Email</span>
                  <span className="v">{u.email}</span>
                </div>
                <div className="det">
                  <span className="k">Phone</span>
                  <span className="v">—</span>
                </div>
                <div className="det">
                  <span className="k">Timezone</span>
                  <span className="v">—</span>
                </div>
                <div className="det">
                  <span className="k">Pronouns</span>
                  <span className="v">—</span>
                </div>
              </div>
            </div>

            <div className="panel">
              <div className="panel-head">
                <h3>Passkeys</h3>
                <Link to="/settings/account" className="lk">
                  Manage
                </Link>
              </div>
              <div className="panel-body">
                <div className="pk">
                  <div className="pk-ic">
                    <Icon name="key" size={17} />
                  </div>
                  <div>
                    <div className="pk-nm">No passkeys yet</div>
                    <div className="pk-meta">
                      Add one in Settings to sign in faster
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute("/profile")({
  component: ProfilePage,
});
